import { useState } from 'react';
import styled from 'styled-components';
import {
  Page,
  Container,
  Section,
  Eyebrow,
  Display,
  SectionTitle,
  Lead,
  Prose,
  Card,
  Grid,
  Mono,
  Tag,
  PrimaryButton,
  CtaBand,
} from '../Layout/PageLayout';
import { useGames, formatCount, GOLDEN_GAMES } from './games';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'full', label: 'Full game' },
  { id: 'update', label: 'Updates' },
  { id: 'fixes', label: 'Bug fixes' },
];

const BLACK_SWAN_HUNTERS = [
  ['Survive the Cold', 'Full game'],
  ['The Slowest Race', 'Full game'],
  ["What's in the BOX?", 'Full game'],
  ['Stranded: Mine to Survive', 'Full game'],
  ['City Go', 'Update'],
  ['Find The Skifidol Brainrot Card', 'Update'],
];

const ANIMATIONS = [
  { title: 'Idle animation', src: '/videos/idle-animation.mp4' },
  { title: 'Sit emote', src: '/videos/sit-emote.mp4' },
  { title: 'Fall emote', src: '/videos/fall-emote.mp4' },
  { title: 'Ground smash', src: '/videos/ground-smash-animation.mp4' },
];

const FilterBar = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const FilterButton = styled.button`
  min-height: 2.75rem;
  padding: 0.625rem 1.125rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${(p) => (p.$on ? 'var(--text-primary)' : 'transparent')};
  color: ${(p) => (p.$on ? 'var(--brand-ink)' : 'var(--text-code)')};
  border: 1px solid ${(p) => (p.$on ? 'var(--text-primary)' : 'var(--border-accent)')};
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--text-primary);
  }

  span {
    opacity: 0.6;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    margin-left: 0.375rem;
  }
`;

const Thumb = styled.div`
  aspect-ratio: ${(p) => p.$ratio || '16 / 9'};
  background: var(--surface-3);
  border-radius: ${(p) => (p.$rounded ? '0.5rem' : 0)};
  overflow: hidden;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const GameBody = styled.div`
  padding: 1.5rem 1.625rem 1.625rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-grow: 1;

  h2 {
    font-size: 1.4375rem;
    line-height: 1.2;
    letter-spacing: -0.01em;
    flex-grow: 1;
  }
`;

const Metrics = styled.div`
  display: flex;
  gap: 1.75rem;
  border-top: 1px solid var(--border);
  padding-top: 1rem;

  strong {
    display: block;
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 600;
  }

  span {
    font-size: 0.8125rem;
    color: var(--text-tertiary);
  }
`;

const StudioCard = styled(Card)`
  padding: 2rem;
  display: flex;
  flex-direction: column;

  header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: 1.5rem;
  }
`;

const TitleList = styled.ul`
  list-style: none;

  li {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 0;
    border-top: 1px solid var(--border);
    font-size: 0.9375rem;
  }

  li span:last-child {
    color: var(--text-tertiary);
    font-size: 0.8125rem;
    white-space: nowrap;
  }
`;

const Portfolio = () => {
  const games = useGames();
  const [filter, setFilter] = useState('all');
  const visible = games.filter((g) => filter === 'all' || g.cat === filter);
  const rocket = games.find((g) => g.id === '9468928549');

  return (
    <Page>
      <Section style={{ paddingBottom: '3.5rem' }}>
        <Container>
          <Eyebrow>Work / games</Eyebrow>
          <Display>Selected engineering work.</Display>
          <Lead>
            These are games where I wrote the main systems or delivered major updates. Visit counts come directly from
            Roblox and refresh every few minutes.
          </Lead>
        </Container>
      </Section>

      <Section $tight>
        <Container>
          <FilterBar role="group" aria-label="Filter by role">
            {FILTERS.map((f) => (
              <FilterButton
                key={f.id}
                type="button"
                $on={filter === f.id}
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
                <span>{f.id === 'all' ? games.length : games.filter((g) => g.cat === f.id).length}</span>
              </FilterButton>
            ))}
          </FilterBar>

          <Grid $min="21.25rem" $gap="1.5rem">
            {visible.map((g) => (
              <Card as="article" key={g.id} style={{ display: 'flex', flexDirection: 'column' }}>
                <Thumb>{g.image && <img src={g.image} alt={`${g.title} thumbnail`} loading="lazy" />}</Thumb>
                <GameBody>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                    <Mono>{g.studio.toUpperCase()}</Mono>
                    <Tag $small>{g.role}</Tag>
                  </div>
                  <h2>{g.title}</h2>
                  <Metrics>
                    <div>
                      <strong>{formatCount(g.visits)}</strong>
                      <span>visits</span>
                    </div>
                    <div>
                      <strong>{formatCount(g.playing)}</strong>
                      <span>playing now</span>
                    </div>
                  </Metrics>
                </GameBody>
              </Card>
            ))}
          </Grid>
        </Container>
      </Section>

      <Section $band style={{ borderBottom: 0 }}>
        <Container>
          <Eyebrow>Studios &amp; teams</Eyebrow>
          <SectionTitle>Work inside studio teams.</SectionTitle>
          <Prose>
            In a studio, my code has to fit into an existing codebase and a shared schedule. These are the teams I have
            worked with.
          </Prose>

          <Grid $min="20rem" $gap="1.5rem">
            <StudioCard>
              <header>
                <h3>Black Swan Hunters</h3>
                <Mono $dim>{BLACK_SWAN_HUNTERS.length} titles</Mono>
              </header>
              <TitleList>
                {BLACK_SWAN_HUNTERS.map(([title, role]) => (
                  <li key={title}>
                    <span>{title}</span>
                    <span>{role}</span>
                  </li>
                ))}
              </TitleList>
            </StudioCard>

            <StudioCard>
              <header>
                <h3>Golden Games</h3>
                <Mono $dim>{GOLDEN_GAMES.length} titles</Mono>
              </header>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem' }}>
                {GOLDEN_GAMES.map((g) => (
                  <div key={g.title} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Thumb $ratio="1" $rounded>
                      <img src={g.icon} alt={`${g.title} icon`} loading="lazy" />
                    </Thumb>
                    <span style={{ fontSize: '0.875rem' }}>{g.title}</span>
                  </div>
                ))}
              </div>
              <Mono $dim style={{ marginTop: '1.25rem' }}>
                Role: scripter
              </Mono>
            </StudioCard>

            <StudioCard>
              <header>
                <h3>Optidash</h3>
                <Mono $dim>1 title</Mono>
              </header>
              <Thumb $rounded style={{ marginBottom: '1rem' }}>
                {rocket.image && <img src={rocket.image} alt="Launch Your Rocket thumbnail" loading="lazy" />}
              </Thumb>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '0.9375rem' }}>
                <span>Launch Your Rocket</span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8125rem' }}>Update</span>
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '2rem',
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  marginTop: 'auto',
                  paddingTop: '1.5rem',
                }}
              >
                {formatCount(rocket.visits)}{' '}
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: '0.875rem', fontWeight: 400, color: 'var(--text-tertiary)' }}>
                  visits
                </span>
              </div>
            </StudioCard>
          </Grid>
        </Container>
      </Section>

      <Section $ruled>
        <Container>
          <Eyebrow>Showcases</Eyebrow>
          <SectionTitle>Movement and animation work.</SectionTitle>
          <Prose style={{ marginBottom: '3rem' }}>
            Some systems are easier to understand in motion. The clips below show gameplay features.
          </Prose>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <Thumb $rounded style={{ flex: '2 1 35rem', minWidth: 0, border: '1px solid var(--border)' }}>
              <video autoPlay loop muted playsInline preload="metadata" disablePictureInPicture>
                <source src="/videos/combat-movement-system.mp4" type="video/mp4" />
              </video>
            </Thumb>
            <div style={{ flex: '1 1 18.75rem', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '1rem' }}>
              <Mono>MAIN SHOWCASE</Mono>
              <h3 style={{ fontSize: '1.875rem' }}>Combat movement system</h3>
              <p>Sprinting and dodging with smooth combo transitions. The controls respond quickly even with network latency.</p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <Tag $small>Input buffering</Tag>
                <Tag $small>State machine</Tag>
                <Tag $small>Animation</Tag>
              </div>
            </div>
          </div>

          <Grid $min="15rem" $gap="1rem">
            {ANIMATIONS.map((a) => (
              <div key={a.title}>
                <Thumb $ratio="1" $rounded style={{ border: '1px solid var(--border)' }}>
                  <video autoPlay loop muted playsInline preload="metadata" disablePictureInPicture>
                    <source src={a.src} type="video/mp4" />
                  </video>
                </Thumb>
                <div style={{ fontSize: '0.875rem', marginTop: '0.625rem' }}>{a.title}</div>
              </div>
            ))}
          </Grid>
        </Container>
      </Section>

      <CtaBand
        title="Do you want to see work similar to your project?"
        text="Send the scope and the technical problem, and I will share relevant examples."
      >
        <PrimaryButton to="/contact">Get in touch</PrimaryButton>
      </CtaBand>
    </Page>
  );
};

export default Portfolio;
